import { createFileRoute } from "@tanstack/react-router";
import React, { useState } from "react";
import { Gift, Heart, Info, Send } from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import { StudentPicker } from "@/components/shared/StudentPicker";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import EmptyState from "@/components/shared/EmptyState";
import { TableSkeleton } from "@/components/shared/Skeletons";
import TipCard from "@/components/shared/TipCard";
import { Field } from "@/components/forms";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  usePartnerSentTips,
  useCreateTip,
  DEMO_PARTNER_SENT_TIPS,
  type Tip,
} from "@/hooks/useTips";
import { usePartnerStudents } from "@/hooks/usePartnerData";
import { useAuthStore } from "@/store/authStore";
import { startSnippeCheckout, usePaymentReturn } from "@/lib/payments";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

// Snippe's smallest payment, in TZS.
const MIN_TIP = 500;

export const Route = createFileRoute("/partner/tips/")({
  component: PartnerTipsPage,
});

const TYPE_OPTIONS = [
  { value: "appreciation", label: "Appreciation" },
  { value: "milestone", label: "Milestone" },
];

function PartnerTipsPage() {
  const { user } = useAuthStore();
  const { data: students = [], isLoading: studentsLoading } = usePartnerStudents();
  const { data: sentTips = [], isLoading: tipsLoading } = usePartnerSentTips();

  const displayTips = user?.isDemo ? DEMO_PARTNER_SENT_TIPS : sentTips;

  const [form, setForm] = useState({
    studentId: "",
    amount: "",
    message: "",
    type: "appreciation",
  });
  const [submitting, setSubmitting] = useState(false);

  const createTip = useCreateTip();
  const queryClient = useQueryClient();

  // Back from Snippe checkout: the tip completes once the payment is confirmed.
  usePaymentReturn(() => {
    queryClient.invalidateQueries({ queryKey: ["tips"] });
  });

  // Only paid tips count; a tip whose checkout was abandoned stays pending.
  const paidTips = displayTips.filter((t: Tip) => t.status === "completed");
  const totalSent = paidTips.reduce((sum: number, t: Tip) => sum + t.amount, 0);

  const formatTZS = (n: number) =>
    new Intl.NumberFormat("en-TZ", {
      style: "currency",
      currency: "TZS",
      minimumFractionDigits: 0,
    }).format(n);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.studentId || !form.amount || !form.message.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }
    const amount = parseFloat(form.amount);
    if (isNaN(amount) || amount < MIN_TIP) {
      toast.error(`A tip must be at least TZS ${MIN_TIP}.`);
      return;
    }
    setSubmitting(true);
    try {
      const tip = await createTip.mutateAsync({
        studentId: Number(form.studentId),
        amount: Math.round(amount),
        message: form.message.trim(),
      });
      if (user?.isDemo) {
        toast.success("Tip sent successfully! ✨");
        setForm({ studentId: "", amount: "", message: "", type: "appreciation" });
        setSubmitting(false);
        return;
      }
      // The tip is pending until it is paid; this leaves the page for Snippe.
      await startSnippeCheckout({ type: "tip", referenceId: Number(tip.id), returnPath: "/partner/tips" });
    } catch (err: unknown) {
      const apiMessage = (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
      const message =
        apiMessage?.error || apiMessage?.message || (err instanceof Error ? err.message : "Failed to send tip. Please try again.");
      toast.error(message);
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Send tips"
        subtitle="Appreciate students for great work with a tip."
        breadcrumbs={[
          { label: "Dashboard", path: "/partner/dashboard" },
          { label: "Send Tips" },
        ]}
      />

      <div className="grid items-start gap-4 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        {/* Send Tip Form */}
        <SectionCard title="Send a tip">
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <Alert variant="info">
              <Info />
              <AlertDescription className="text-foreground">
                Tips are a great way to motivate students and recognize their hard work.
              </AlertDescription>
            </Alert>

            <Field label="Select student">
              {({ id }) => (
                <StudentPicker
                  id={id}
                  value={form.studentId || null}
                  onChange={(v) => setForm((f) => ({ ...f, studentId: v ?? "" }))}
                  placeholder="Choose a student..."
                  subtitle="Pick the student you want to tip."
                  options={(students as { id?: string | number; student_id?: string | number; name?: string; student_name?: string; full_name?: string }[]).map((s) => ({
                    value: String(s.id ?? s.student_id),
                    label: s.name ?? s.student_name ?? s.full_name ?? `Student #${s.id ?? s.student_id}`,
                  }))}
                />
              )}
            </Field>

            <Field
              label="Amount"
              hint={`Minimum TZS ${MIN_TIP}. You'll pay on Snippe's secure checkout.`}
            >
              {({ id, describedBy }) => (
                <div className="relative">
                  <span
                    className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 font-mono text-caption text-muted-foreground"
                    aria-hidden
                  >
                    TZS
                  </span>
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    type="number"
                    value={form.amount}
                    onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                    placeholder="e.g. 5000"
                    min={MIN_TIP}
                    step={100}
                    className="pl-11 tabular-nums"
                  />
                </div>
              )}
            </Field>

            <Field label="Tip type">
              {({ id }) => (
                <Select
                  value={form.type}
                  onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}
                >
                  <SelectTrigger id={id}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            <Field label="Message">
              <Textarea
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                rows={3}
                placeholder="Write a short message of appreciation..."
              />
            </Field>

            <Button type="submit" loading={submitting || createTip.isPending} className="mt-1">
              {!(submitting || createTip.isPending) && <Send />}
              {submitting || createTip.isPending ? "Opening payment..." : "Pay & Send Tip"}
            </Button>
          </form>
        </SectionCard>

        {/* Sent Tips History */}
        <div className="flex min-w-0 flex-col gap-4">
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            <StatCard label="Total sent" value={paidTips.length} icon={<Heart />} />
            <StatCard label="Total amount" value={formatTZS(totalSent)} icon={<Gift />} />
          </div>

          <SectionCard title="Sent tips history" noPadding>
            {tipsLoading ? (
              <div className="p-4">
                <TableSkeleton rows={5} columns={4} />
              </div>
            ) : displayTips.length === 0 ? (
              <EmptyState
                bare
                icon={<Gift />}
                title="No tips sent yet"
                description="Use the form to send your first tip and appreciate a student."
              />
            ) : (
              <div className="flex flex-col gap-3 p-4 md:p-5">
                {displayTips.map((tip) => (
                  <TipCard key={tip.id} tip={tip} variant="full" showStudent />
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
